import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ResultsContainer,
  PageMargin,
  ResultsWrapper,
  ResultsPadding,
  Divider,
  GroupTitleWrapper,
  ModelTitleWrapper,
  NoOffersWrapper,
} from './OffersListContainer.style';
import { CopyOne, HeadlineOrderTwo } from '../Common/Common.styles';
import { GroupedOffers } from '../../utils/groupOffers';
import { FilterOption } from '../../types';
import VehicleOffersList from '../VehicleOffersList/VehicleOffersList';

const DEFAULT_SORTING = ['etron', 'q/sq', 'a/s', 'r/rs', 'tt'];

type OffersListContainerProps = {
  modelFamilyOptions: {
    [key: string]: FilterOption;
  };
  groupedModels: {
    [key: string]: string[];
  };
  offers: GroupedOffers;
  handleSectionRef: (key: string) => (el: HTMLDivElement | null) => void;
  isAnyFilterSelected: boolean;
  isFiltersOpen: boolean;
};

const OffersListContainer: React.FC<OffersListContainerProps> = ({
  modelFamilyOptions,
  groupedModels,
  offers,
  handleSectionRef,
  isAnyFilterSelected,
  isFiltersOpen,
}) => {
  const { t } = useTranslation();

  const renderContent = useMemo(() => {
    if (!Object.keys(offers).length) return null;
    const familyGroupKeys = Object.keys(groupedModels);
    const sortedGroupKeys = familyGroupKeys.sort((a, b) => {
      // Get the index of each element in the DEFAULT_SORTING array
      const indexA = DEFAULT_SORTING.indexOf(a);
      const indexB = DEFAULT_SORTING.indexOf(b);

      // Compare the indices
      return indexA - indexB;
    });
    return sortedGroupKeys.map((groupItem) => {
      const subModelObject = offers[groupItem];
      const subModelKeys = Object.keys(subModelObject);
      if (isAnyFilterSelected && !subModelKeys.length) return null;
      return (
        <div
          ref={handleSectionRef(`${groupItem}-model`)}
          id={`${groupItem}-model`}
          key={`${groupItem}-key`}
          data-testid={`${groupItem}-section`}
        >
          <ResultsPadding>
            <GroupTitleWrapper>
              {/* render group model family title */}
              <HeadlineOrderTwo data-testid={`${groupItem}-modelGroup-title`}>
                {t(`promo.offers.modelGroup.${groupItem}`)}
              </HeadlineOrderTwo>
            </GroupTitleWrapper>
            {subModelKeys?.length ? (
              <>
                {subModelKeys.map((modelFamilyKey) => {
                  const offersToRender = subModelObject[modelFamilyKey];
                  return (
                    <VehicleOffersList
                      offersToRender={offersToRender}
                      modelFamilyKey={modelFamilyKey}
                      modelFamilyOptions={modelFamilyOptions}
                      key={modelFamilyKey}
                      isAnyFilterSelected={isAnyFilterSelected}
                      isFiltersOpen={isFiltersOpen}
                    />
                  );
                })}
              </>
            ) : (
              <>
                <ModelTitleWrapper>
                  <CopyOne data-testid={`${groupItem}-zero-count`}>
                    0 {t('promo.offers.availableCount')}
                  </CopyOne>
                </ModelTitleWrapper>
                <NoOffersWrapper>
                  <CopyOne data-testid="no-offers-disclaimer">
                    {t('promo.offers.noResultDisclaimer')}
                  </CopyOne>
                </NoOffersWrapper>
                <Divider />
              </>
            )}
          </ResultsPadding>
        </div>
      );
    });
  }, [
    groupedModels,
    handleSectionRef,
    isAnyFilterSelected,
    isFiltersOpen,
    modelFamilyOptions,
    offers,
    t,
  ]);

  return (
    <>
      <ResultsContainer>
        <PageMargin>
          <ResultsWrapper data-testid="results-wrapper">{renderContent}</ResultsWrapper>
        </PageMargin>
      </ResultsContainer>
    </>
  );
};

export default OffersListContainer;
